import { FC, useEffect } from 'react';
import MainLayout from 'components/main-layout/MainLayout';
import { useQuery } from 'react-query';
import API from 'core/Api';
import { useHistory, useLocation, useParams } from 'react-router';
import { ISelectOptionProps } from 'components/form/types';
import IField from 'components/fields/types';
import { sortAZ } from 'utils/helpers/sortAZ';
import { useActiveClientContext } from 'store/active-client';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import TokensForm from './TokensForm';
import { IToken } from '../types';

interface IProps {
  tokensEntityName: string;
  tokensEndpoint: string;
  fieldsEntityName: string;
  fieldsEndpoint: string;
  tokenIdKey: string;
  fieldIdKey: string;
  templateFilter: string;
}

const ManageTokens: FC<IProps> = ({
  tokensEntityName,
  tokensEndpoint,
  fieldsEntityName,
  fieldsEndpoint,
  tokenIdKey,
  fieldIdKey,
  templateFilter,
}) => {
  const { id } = useParams<{ id: string | undefined }>();
  const location = useLocation<{ isEdit?: boolean; templateName?: string }>();
  const { state } = location;

  const { client } = useActiveClientContext();

  const history = useHistory();

  useEffect(() => {
    /**
     * If the user has reloaded the page and location state is missing
     * send him back to editing the template with `id`
     */
    if (!state) history.replace(buildClientUrl(`/document-templates/edit/${id}`, client));
  }, []);

  const {
    isLoading: isLoadingTokens,
    error: errorTokens,
    data: tokens,
  } = useQuery<IToken[], Error>(
    [buildClientEntity(tokensEntityName, client), { [templateFilter]: id }],
    (): Promise<IToken[]> => {
      return API.get(`${tokensEndpoint}?${templateFilter}=${id}`).then(res => res.data?.items);
    },
    {
      cacheTime: 0,
    }
  );

  const {
    isLoading: isLoadingFields,
    error: errorFields,
    data: fields,
  } = useQuery<ISelectOptionProps[], Error>(
    buildClientEntity(fieldsEntityName, client),
    (): Promise<ISelectOptionProps[]> => {
      return API.get(`${fieldsEndpoint}?is_active=true`).then(res =>
        res.data?.items?.map((field: IField) => ({
          label: field.name,
          value: field.id,
        }))
      );
    }
  );

  return (
    <MainLayout
      title="Управление на токени на шаблон за документ"
      isLoading={isLoadingTokens || isLoadingFields}
      error={errorTokens || errorFields}
    >
      {tokens && fields && (
        <TokensForm
          tokensEntityName={tokensEntityName}
          tokens={tokens}
          fields={sortAZ(fields, 'label')}
          isEditMode={state?.isEdit}
          templateName={state?.templateName}
          tokenIdKey={tokenIdKey}
          fieldIdKey={fieldIdKey}
          templateFilter={templateFilter}
        />
      )}
    </MainLayout>
  );
};

export default ManageTokens;
